import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsApi } from 'entities/Settings'
import {
  type IParamsPostChats,
  type IResponseWebsiteChatWidgetSettings,
} from 'entities/Settings/api/types'
import {
  CustomizeBlockStore,
  applyOrgNamePlaceholder,
} from 'pages/settings/pages/widgetChat/store/CustomizeBlockStore'
import { ConversationsBlockStore } from 'pages/settings/pages/widgetChat/store/ConversationsBlockStore'
import { InstallBlockStore } from 'pages/settings/pages/widgetChat/store/InstallBlockStore'

export class WidgetChatStore {
  initialLoading = true
  websiteChatWidgetSettings: IResponseWebsiteChatWidgetSettings | null = null
  loadingAction: 'disable' | 'publish' | null = null
  isChatVisible = false

  customizeBlockStore: CustomizeBlockStore
  conversationsBlockStore: ConversationsBlockStore
  installBlockStore: InstallBlockStore

  private _disposeOrgNameReaction: (() => void) | null = null

  constructor(private _entities: IEntitiesStore) {
    const orgName = this._entities.organizationStore.name ?? null
    this.customizeBlockStore = new CustomizeBlockStore(orgName)
    this.installBlockStore = new InstallBlockStore(this._entities)
    this.conversationsBlockStore = new ConversationsBlockStore(this._entities)
    makeAutoObservable(this)

    this._disposeOrgNameReaction = reaction(
      () => this._entities.organizationStore.name,
      (name) => {
        const settings = this.websiteChatWidgetSettings
        if (!name || settings === null) return
        if (!this.customizeBlockStore.opt_in_text.includes('{org_name}')) return

        const next = applyOrgNamePlaceholder(this.customizeBlockStore.opt_in_text, name)
        const nextTruncated = next.slice(0, 500)

        if (nextTruncated === this.customizeBlockStore.opt_in_text) return

        runInAction(() => {
          this.websiteChatWidgetSettings = {
            ...settings,
            opt_in_text: nextTruncated,
          }
          this.customizeBlockStore.setOptInText(nextTruncated)
        })
      }
    )

    this.init()
  }

  private normalizeWidgetSettingsOptInText(
    data: IResponseWebsiteChatWidgetSettings
  ): IResponseWebsiteChatWidgetSettings {
    const orgName = this._entities.organizationStore.name
    const opt_in_text = applyOrgNamePlaceholder(data.opt_in_text, orgName)
    return opt_in_text === data.opt_in_text ? data : { ...data, opt_in_text }
  }

  private syncBlockStoresFromSettings(settings: IResponseWebsiteChatWidgetSettings) {
    this.customizeBlockStore.syncFromApi({
      color: settings.color,
      position: settings.position,
      header: settings.header,
      message: settings.message,
      button: settings.button,
      opt_in_text: settings.opt_in_text,
    })
    this.conversationsBlockStore.syncFromApi(settings)
    this.installBlockStore.syncFromApi(settings.domains)
  }

  private isEmptyChatWidgetSettingsResponse(
    value: IResponseWebsiteChatWidgetSettings | Record<string, never>
  ): value is Record<string, never> {
    if (!value || typeof value !== 'object') return false

    return Object.keys(value).length === 0
  }

  get entities() {
    return this._entities
  }

  get isActionLoading() {
    return this.loadingAction !== null
  }

  private static payloadFromSettings(
    settings: IResponseWebsiteChatWidgetSettings
  ): IParamsPostChats {
    return {
      button: settings.button,
      color: settings.color,
      domains: settings.domains,
      header: settings.header,
      message: settings.message,
      opt_in_text: settings.opt_in_text,
      position: settings.position,
      status: settings.status,
      team_id: String(settings.team_id),
      tags: (settings.tags ?? []).map((t) => t.id),
    }
  }

  private static payloadForCompare(p: IParamsPostChats): IParamsPostChats {
    return { ...p, tags: [...p.tags].sort((x, y) => x - y) }
  }

  private showGenericErrorToast(): void {
    showToast({
      type: 'error',
      title: 'Something went wrong',
      desc: 'Please try again',
    })
  }

  get hasUnsavedChanges(): boolean {
    const settings = this.websiteChatWidgetSettings

    if (settings === null) return false

    const current = this.getPostChatsPayload(settings.status)
    const saved = WidgetChatStore.payloadFromSettings(settings)

    return !isEqual(
      WidgetChatStore.payloadForCompare(current),
      WidgetChatStore.payloadForCompare(saved)
    )
  }

  setIsChatVisible = (value: boolean) => {
    this.isChatVisible = value
  }

  openDisableConfirmModal = () => {
    const modalId = nanoid()
    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.ALERT,
      title: 'Disable chat widget?',
      desc: "This will remove the chat widget from all websites where it's currently installed.",
      showCloseButton: false,
      primaryAction: {
        text: 'Yes, disable',
        onAction: async () => {
          modalStore.closeModal(modalId)
          await this.disableChatsStatus()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  handlePublish = async () => {
    runInAction(() => {
      this.loadingAction = 'publish'
    })
    try {
      await this.handlePublishChanges()
    } catch (error) {
      logger.error(error)
      this.showGenericErrorToast()
    } finally {
      runInAction(() => {
        this.loadingAction = null
      })
    }
  }

  init = async () => {
    await this.getWebsiteChatWidgetSettings()
  }

  getWebsiteChatWidgetSettings = async () => {
    try {
      const { data } = await SettingsApi.getWebsiteChatWidgetSettings()
      runInAction(() => {
        if (!this.isEmptyChatWidgetSettingsResponse(data)) {
          const normalized = this.normalizeWidgetSettingsOptInText(data)

          this.websiteChatWidgetSettings = normalized
          this.syncBlockStoresFromSettings(normalized)
        }

        this.initialLoading = false
      })
    } catch (error) {
      logger.error(error)
      this.showGenericErrorToast()
      runInAction(() => {
        this.initialLoading = false
      })
    }
  }

  getPostChatsPayload(status?: boolean): IParamsPostChats {
    const settings = this.websiteChatWidgetSettings
    const customize = this.customizeBlockStore.getSnapshot()
    const install = this.installBlockStore.getSnapshot()

    return {
      button: customize.button,
      color: customize.color,
      domains: install.domains,
      header: customize.header,
      message: customize.message,
      status: status ?? settings?.status ?? false,
      position: customize.position,
      team_id: String(this.conversationsBlockStore.teamId ?? settings?.team_id),
      opt_in_text: customize.opt_in_text,
      tags: this.conversationsBlockStore.tagsControlStore.tags.map((t) => t.id),
    }
  }

  disableChatsStatus = async () => {
    runInAction(() => {
      this.loadingAction = 'disable'
    })
    try {
      const { data } = await SettingsApi.updateChatsStatus(false)
      runInAction(() => {
        const normalized = this.normalizeWidgetSettingsOptInText(data)
        this.websiteChatWidgetSettings = normalized
        this.syncBlockStoresFromSettings(normalized)
      })
      showToast({ type: 'info', title: 'Website chat widget disabled' })
    } catch (error) {
      logger.error(error)
      this.showGenericErrorToast()
    } finally {
      runInAction(() => {
        this.loadingAction = null
      })
    }
  }

  handlePublishChanges = async () => {
    const customizeValid = this.customizeBlockStore.validate(this.conversationsBlockStore.teamId)

    if (!customizeValid) {
      const isActivating = !this.websiteChatWidgetSettings?.status
      showToast({
        type: 'error',
        title: isActivating ? 'Failed to activate' : 'Failed to publish changes',
        desc: 'Complete all required fields',
      })
      return
    }

    try {
      const payload = this.getPostChatsPayload(true)
      const { data } = await SettingsApi.postChats(payload)
      const wasInactive = !this.websiteChatWidgetSettings?.status
      runInAction(() => {
        const normalized = this.normalizeWidgetSettingsOptInText(data)
        this.websiteChatWidgetSettings = normalized
        this.syncBlockStoresFromSettings(normalized)
      })
      if (wasInactive) {
        showToast({ type: 'success', title: 'Website chat widget activated' })
      } else {
        showToast({ type: 'success', title: 'Website chat widget changes published' })
      }
    } catch (e) {
      logger.error(e)

      this.showGenericErrorToast()

      if (e instanceof AxiosError) {
        const invalidIndexes = e.errors
          .keys()
          .filter((errorKey) => errorKey.startsWith('domains.'))
          .map((errorKey) => parseInt(errorKey.replace('domains.', ''), 10))

        if (invalidIndexes.length > 0) {
          runInAction(() => {
            this.installBlockStore.setDomainErrorsForIndexes(invalidIndexes)
          })
        }
      }
    }
  }

  dispose = () => {
    this._disposeOrgNameReaction?.()
    this._disposeOrgNameReaction = null
  }
}
